import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdLocationOn, MdBusiness, MdLayers, MdMeetingRoom, MdRefresh, MdCalendarToday, MdCheckCircle, MdBlock, MdAccessTime, MdViewModule, MdViewList, MdPeople } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import BookingModal from '../components/BookingModal';
import { conferenceRoomApi, amenityApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './RoomAvailability.css';
import './BookRoom.css';

function RoomAvailability() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCapacity, setFilterCapacity] = useState('');
  const [filterAmenities, setFilterAmenities] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [availableAmenities, setAvailableAmenities] = useState([]);

  useEffect(() => {
    loadData();
    loadAmenities();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAmenities = async () => {
    try {
      const data = await amenityApi.getActive();
      const amenitiesList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setAvailableAmenities(amenitiesList);
    } catch (error) {
      console.error('Failed to load amenities:', error);
    }
  };

  const getAmenityIcon = (amenityName) => {
    const amenity = availableAmenities.find(a => a.name.toLowerCase() === amenityName.toLowerCase());
    return amenity?.icon || '✓';
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const roomsData = await conferenceRoomApi.getAll(true); // includeBookings = true

      const roomsList = Array.isArray(roomsData?.data) ? roomsData.data :
                       Array.isArray(roomsData) ? roomsData :
                       Array.isArray(roomsData?.content) ? roomsData.content : [];

      // Extract all bookings from rooms
      const allBookings = [];
      roomsList.forEach(room => {
        if (room.latestBookings && Array.isArray(room.latestBookings)) {
          room.latestBookings.forEach(booking => {
            allBookings.push({
              ...booking,
              roomId: room.id,
              roomName: room.roomName
            });
          });
        }
      });

      setRooms(roomsList.filter(r => r.status === 'ACTIVE'));
      setBookings(allBookings);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load workspace availability data', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get current booking status for a workspace
  const getRoomStatus = (room) => {
    const now = new Date();
    const currentBookings = bookings.filter(booking => {
      if (booking.roomId !== room.id) return false;
      if (booking.status !== 'APPROVED') return false;

      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      return now >= start && now <= end;
    });

    if (currentBookings.length > 0) {
      return { status: 'occupied', booking: currentBookings[0] };
    }

    // Check for upcoming bookings in next 30 minutes
    const upcomingBookings = bookings.filter(booking => {
      if (booking.roomId !== room.id) return false;
      if (booking.status !== 'APPROVED') return false;

      const start = new Date(booking.startTime);
      const minutesUntilStart = (start - now) / 1000 / 60;
      return minutesUntilStart > 0 && minutesUntilStart <= 30;
    });

    if (upcomingBookings.length > 0) {
      return { status: 'upcoming', booking: upcomingBookings[0] };
    }

    return { status: 'available', booking: null };
  };

  // Get next available time for a workspace
  const getNextAvailableTime = (room) => {
    const now = new Date();
    const roomBookings = bookings
      .filter(b => b.roomId === room.id && b.status === 'APPROVED')
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    for (const booking of roomBookings) {
      const start = new Date(booking.startTime);
      if (start > now) {
        const minutesUntil = Math.floor((start - now) / 1000 / 60);
        if (minutesUntil < 60) {
          return `${minutesUntil} min`;
        } else if (minutesUntil < 1440) {
          return `${Math.floor(minutesUntil / 60)}h ${minutesUntil % 60}m`;
        } else {
          return `${Math.floor(minutesUntil / 1440)}d`;
        }
      }
    }
    return 'All day';
  };

  // Filter workspaces based on criteria
  const filteredRooms = useMemo(() => {
    return rooms.filter(room => {
      // Location filter
      if (filterLocation && !room.location.toLowerCase().includes(filterLocation.toLowerCase())) {
        return false;
      }

      // Capacity filter
      if (filterCapacity) {
        const capacity = parseInt(filterCapacity);
        if (room.capacity < capacity) return false;
      }

      // Amenities filter
      if (filterAmenities.length > 0) {
        const roomAmenities = (room.amenities || '').toLowerCase();
        return filterAmenities.every(amenity =>
          roomAmenities.includes(amenity.toLowerCase())
        );
      }

      return true;
    });
  }, [rooms, filterLocation, filterCapacity, filterAmenities]);

  // Get unique locations and amenities for filters
  const locations = [...new Set(rooms.map(r => r.location))];
  const allAmenities = [...new Set(
    rooms.flatMap(r => (r.amenities || '').split(',').map(a => a.trim()).filter(Boolean))
  )];

  const handleBookRoom = (room) => {
    const roomStatus = getRoomStatus(room);
    if (roomStatus.status === 'occupied') {
      toast.warning('This workspace is currently occupied', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setSelectedRoom(room);
    setShowBookingModal(true);
  };

  const handleViewDetails = (room) => {
    navigate(`/admin/community/conference-rooms/${room.id}`);
  };

  const toggleAmenityFilter = (amenity) => {
    setFilterAmenities(prev =>
      prev.includes(amenity)
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return '#10b981';
      case 'occupied': return '#ef4444';
      case 'upcoming': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <MdCheckCircle />;
      case 'occupied': return <MdBlock />;
      case 'upcoming': return <MdAccessTime />;
      default: return <MdMeetingRoom />;
    }
  };

  if (isLoading && rooms.length === 0) {
    return (
      <div className="admin-view conference-rooms-view">
        <PageHeader breadcrumbs={["Community", "Workspace Availability"]} />
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <div className="loading-spinner-large"></div>
          <p>Loading workspace availability...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view conference-rooms-view">
      <PageHeader breadcrumbs={["Community", "Workspace Availability"]} />

      {/* Combined Header and Filters Row */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdMeetingRoom className="title-icon" />
            Workspace Availability Dashboard
          </h2>
        </div>

        <div className="header-filters-compact">
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="input select-compact"
          >
            <option value="">Location</option>
            {locations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>

          <input
            type="number"
            value={filterCapacity}
            onChange={(e) => setFilterCapacity(e.target.value)}
            placeholder="Min Capacity"
            className="input search-compact"
            min="1"
            style={{ width: '140px' }}
          />

          <div className="view-toggle-compact">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <MdViewModule />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <MdViewList />
            </button>
          </div>

          <button
            className="btn-book-compact"
            onClick={loadData}
            disabled={isLoading}
            style={{ marginLeft: '0.5rem' }}
          >
            <MdRefresh />
            <span>{isLoading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Amenities Filter Row */}
      {allAmenities.length > 0 && (
        <div className="amenities-filter-row">
          <label className="amenities-label">Filter by Amenities:</label>
          <div className="amenities-tags">
            {allAmenities.map(amenity => (
              <button
                key={amenity}
                className={`amenity-tag ${filterAmenities.includes(amenity) ? 'active' : ''}`}
                onClick={() => toggleAmenityFilter(amenity)}
              >
                {amenity}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Workspaces Display */}
      <div className={`rooms-container ${viewMode}`}>
        {filteredRooms.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Workspaces Available</h3>
            <p>
              {filterLocation || filterCapacity || filterAmenities.length > 0
                ? 'Try adjusting your filters to see more results'
                : 'There are currently no active workspaces in the system'}
            </p>
          </div>
        ) : (
          filteredRooms.map(room => {
            const roomStatus = getRoomStatus(room);
            const nextAvailable = getNextAvailableTime(room);

            return (
              <div key={room.id} className={`room-card ${roomStatus.status}`}>
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(roomStatus.status) }}
                >
                  <span className="status-icon">{getStatusIcon(roomStatus.status)}</span>
                  <span className="status-text">
                    {roomStatus.status === 'available' ? 'Available' :
                     roomStatus.status === 'occupied' ? 'Occupied' : 'Upcoming'}
                  </span>
                </div>

                <div className="room-info">
                  <h3 className="room-name">
                    <MdMeetingRoom style={{ marginRight: '8px', color: '#704493' }} />
                    {room.roomName}
                  </h3>
                  <div className="room-details">
                    <span className="detail-item">
                      <MdLocationOn /> {room.location}
                    </span>
                    <span className="detail-item">
                      <MdBusiness /> {room.tower} - Floor {room.floor}
                    </span>
                    <span className="detail-item">
                      <MdPeople /> Capacity: {room.capacity}
                    </span>
                  </div>

                  {room.amenities && (
                    <div className="room-amenities">
                      {room.amenities.split(',').map((amenity, idx) => {
                        const trimmedAmenity = amenity.trim();
                        return (
                          <span key={idx} className="amenity-badge">
                            <span style={{ marginRight: '4px' }}>{getAmenityIcon(trimmedAmenity)}</span>
                            {trimmedAmenity}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {roomStatus.status === 'available' && (
                    <div className="availability-info available">
                      <MdCheckCircle style={{ marginRight: '6px' }} />
                      <span>Available for: {nextAvailable}</span>
                    </div>
                  )}

                  {roomStatus.status === 'occupied' && roomStatus.booking && (
                    <div className="availability-info occupied">
                      <MdBlock style={{ marginRight: '6px' }} />
                      <span>Until: {new Date(roomStatus.booking.endTime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}

                  {roomStatus.status === 'upcoming' && roomStatus.booking && (
                    <div className="availability-info upcoming">
                      <MdAccessTime style={{ marginRight: '6px' }} />
                      <span>Booked at: {new Date(roomStatus.booking.startTime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                </div>

                <div className="room-actions">
                  <button
                    className="btn-book-action"
                    onClick={() => handleBookRoom(room)}
                    disabled={roomStatus.status === 'occupied'}
                  >
                    {roomStatus.status === 'available' ? 'Book Now' : 'Book Later'}
                  </button>
                  <button
                    className="btn-details"
                    onClick={() => handleViewDetails(room)}
                  >
                    Details
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedRoom && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedRoom(null);
          }}
          selectedRoom={selectedRoom}
          onBookingSuccess={() => {
            loadData();
            toast.success('Booking created successfully!', {
              position: "top-right",
              autoClose: 3000,
            });
          }}
        />
      )}
    </div>
  );
}

export default RoomAvailability;
